import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { AlertCircle, Plus, RefreshCw, TrendingUp } from "lucide-react"
import { useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Exportar } from "@/componentes/exportar/Exportar"
import type { DocumentoExportable } from "@/componentes/exportar/modelo"
import { DialogoTasa } from "@/componentes/tasas/DialogoTasa"
import { comoVariacion } from "@/componentes/tasas/formato"
import { HistorialTasa } from "@/componentes/tasas/HistorialTasa"
import { TarjetaTasa } from "@/componentes/tasas/TarjetaTasa"
import type { PanelTasas, Sincronizacion } from "@/componentes/tasas/tipos"
import { Boton } from "@/componentes/ui/Boton"
import { tienePermiso } from "@/lib/autorizacion"
import { usarSesion } from "@/lib/sesion"

/**
 * Las tasas de cambio, con el 2.0 y `constructora_v2` de referencia: una tarjeta por
 * divisa con lo que cuesta en bolívares, la brecha del USDT y, debajo, el historial de
 * la elegida con su gráfico y su tabla.
 *
 * ⚠ **Registrar a mano sigue existiendo aunque la consulta sea automática.** El BCV no
 * publica los fines de semana ni los feriados, CotizaVE puede estar caída, y a veces
 * hace falta asentar la tasa que de verdad se usó. Sin esta vía, un día sin API es un
 * día sin poder liquidar.
 *
 * ⚠ **Una consulta que no registra nada NO es un fallo.** Si el BCV no movió el precio,
 * la consulta salió bien: se distingue lo registrado, lo que no cambió y lo que no se
 * pudo, en vez de decir «error».
 *
 * ⚠ **USD no tiene tarjeta: es la referencia**, vale 1 por definición y cada tasa se
 * guarda contra él. Su precio en bolívares es la tarjeta del dólar BCV.
 */
export function Tasas() {
  const clienteQuery = useQueryClient()
  const usuario = usarSesion((e) => e.usuario)
  const puedeRegistrar = tienePermiso(usuario, "tesoreria:registrar_tasa")
  const [elegida, setElegida] = useState<string | null>(null)
  const [registrando, setRegistrando] = useState(false)

  const panel = useQuery({
    queryKey: ["tasas-panel"],
    queryFn: () => api.get<PanelTasas>("/api/v1/tasas/panel"),
  })

  const sincronizar = useMutation({
    mutationFn: () => api.post<Sincronizacion>("/api/v1/tasas/sincronizar"),
    onSuccess: () => {
      for (const clave of ["tasas-panel", "tasas-historial", "tasas-vigentes"]) {
        clienteQuery.invalidateQueries({ queryKey: [clave] })
      }
    },
  })

  const tasas = panel.data?.tasas ?? []
  // Derivada y no guardada: la primera se elige sola, sin un efecto que llegue tarde.
  const actual = tasas.find((t) => t.codigo === elegida) ?? tasas[0] ?? null
  const resultado = sincronizar.data

  return (
    <div className="grid grid-cols-1 gap-4">
      {/* Ni título ni frase propios: los pone la cabecera de «Tesorería». Aquí arriba
          sólo van las acciones, a la derecha. */}
      {(puedeRegistrar || tasas.length > 0) && (
        <div className="flex flex-wrap justify-end gap-2">
          {tasas.length > 0 && (
            <Exportar
              titulo="Tasas de cambio vigentes"
              etiqueta="Exportar tasas"
              subtitulo="Bolívares por 1 unidad de cada divisa, la última registrada"
              documento={() => documentoDeTasas(tasas, panel.data?.brecha ?? null)}
            />
          )}
          {puedeRegistrar && (
            <>
              <Boton
                variante="secundario"
                onClick={() => sincronizar.mutate()}
                disabled={sincronizar.isPending}
              >
                <RefreshCw size={16} aria-hidden={true} />
                {sincronizar.isPending ? "Consultando…" : "Consultar ahora"}
              </Boton>
              <Boton variante="primario" onClick={() => setRegistrando(true)} disabled={!actual}>
                <Plus size={16} aria-hidden={true} />
                Registrar a mano
              </Boton>
            </>
          )}
        </div>
      )}

      {resultado && !resultado.ok && (
        <p
          role="alert"
          className="flex items-start gap-2 text-sm"
          style={{ color: "var(--destructive)" }}
        >
          <AlertCircle size={16} aria-hidden={true} className="mt-0.5 shrink-0" />
          {resultado.error}
        </p>
      )}
      {resultado?.ok && (
        <p role="status" className="text-sm" style={{ color: "var(--fg-muted)" }}>
          {resultado.registradas.length > 0
            ? `Registradas ${resultado.registradas.length}.`
            : "Ninguna cambió."}
          {resultado.sin_cambio.length > 0 && ` Sin cambio: ${resultado.sin_cambio.join(", ")}.`}
          {resultado.omitidas.length > 0 && ` No se pudo: ${resultado.omitidas.join("; ")}.`}
        </p>
      )}
      {sincronizar.isError && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          {sincronizar.error instanceof ErrorApi
            ? sincronizar.error.message
            : "No se pudo consultar"}
        </p>
      )}

      {panel.isPending && (
        <p className="text-sm" style={{ color: "var(--fg-muted)" }}>
          Cargando…
        </p>
      )}
      {panel.isError && (
        <p role="alert" className="text-sm" style={{ color: "var(--destructive)" }}>
          No se pudieron cargar las tasas
        </p>
      )}
      {panel.data && tasas.length === 0 && (
        <p className="tarjeta text-sm" style={{ color: "var(--fg-muted)" }}>
          No hay divisas activas además de la de referencia.
        </p>
      )}

      {tasas.length > 0 && (
        <>
          <ul
            aria-label="Tasas vigentes"
            className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4"
          >
            {tasas.map((t) => (
              <li key={t.codigo}>
                <TarjetaTasa
                  tasa={t}
                  elegida={actual?.codigo === t.codigo}
                  onElegir={() => setElegida(t.codigo)}
                />
              </li>
            ))}
            {panel.data?.brecha != null && (
              <li>
                {/* No es una divisa ni se elige: es la resta de dos tarjetas, y por eso
                    no lleva historial propio. */}
                <div className="tarjeta flex h-full flex-col gap-2" data-testid="tasa-brecha">
                  <span className="flex items-center gap-2 font-semibold">
                    <TrendingUp size={16} aria-hidden={true} />
                    Brecha USDT / BCV
                  </span>
                  <span className="text-2xl font-semibold">{comoVariacion(panel.data.brecha)}</span>
                  <span className="text-xs" style={{ color: "var(--fg-muted)" }}>
                    Lo que cuesta el USDT por encima del dólar oficial, con las dos tasas vigentes.
                  </span>
                </div>
              </li>
            )}
          </ul>

          {actual && <HistorialTasa key={actual.codigo} tasa={actual} />}
        </>
      )}

      {registrando && actual && (
        <DialogoTasa tasas={tasas} inicial={actual.codigo} onCerrar={() => setRegistrando(false)} />
      )}
    </div>
  )
}

/** Las tarjetas de arriba, una fila por divisa, y la brecha al pie. */
function documentoDeTasas(tasas: PanelTasas["tasas"], brecha: string | null): DocumentoExportable {
  return {
    titulo: "Tasas de cambio vigentes",
    subtitulo: "Bolívares por 1 unidad de cada divisa, la última registrada",
    secciones: [
      {
        titulo: "Tasas vigentes",
        columnas: [
          { titulo: "Divisa", tipo: "texto" },
          { titulo: "Fecha de valor", tipo: "fecha" },
          { titulo: "Bs por 1 unidad", tipo: "monto" },
          { titulo: "Variación contra el día anterior (%)", tipo: "numero" },
          { titulo: "Fuente", tipo: "texto" },
        ],
        filas: tasas.map((t) => ({
          celdas: [
            `${t.nombre} (${t.moneda})`,
            t.actual?.fecha_valor ?? null,
            t.actual?.bs_por_unidad == null ? null : Number(t.actual.bs_por_unidad),
            t.variacion === null ? null : Number(t.variacion),
            t.actual?.fuente ?? null,
          ],
        })),
        pie:
          brecha === null
            ? undefined
            : [[`Brecha USDT / BCV: ${comoVariacion(brecha)}`, null, null, null, null]],
      },
    ],
  }
}
